import { ReactNode, useState } from 'react';

import { Notice } from './styles';

import Button from '@/components/Button';
import Modal from '@/components/Modal';
import { JourneyChanges, lifecycleErrorMessage } from '@/services/lifecycleAPI';
import styled from '@/styles';

const Body = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  padding: '18px 24px',
});

const Actions = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '14px 24px',
  borderTop: '1px solid #EDEDED',
});

type Built =
  | { ok: true; changes: JourneyChanges }
  | { ok: false; error: string };

type Props = {
  testId: string;
  title: string;
  size?: 'xs' | 'sm';
  build: () => Built;
  // The settings version the form's values were read from.
  version?: string;
  onSave: (changes: JourneyChanges) => Promise<void>;
  onClose: () => void;
  children: ReactNode;
};

const SettingsDialog = ({
  testId,
  title,
  size = 'sm',
  build,
  version,
  onSave,
  onClose,
  children,
}: Props) => {
  // The form keeps the values it opened with, so it saves with the version
  // they came from. A reload landing while it is open (a stats refresh, a
  // switch's save) brings a newer version; sending that one let graphy accept
  // these old values over another admin's change (review CD6-02).
  const [openedVersion] = useState(version);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After a 409 the page has reloaded behind this dialog, but the form still
  // holds the values it opened with. Saving again would send them with the new
  // version and replace the other admin's change after all (bug hunt 20), so
  // Save stays off until the dialog is opened again on the fresh settings.
  const [stale, setStale] = useState(false);

  const save = async () => {
    const result = build();
    if (!result.ok) {
      setError(result.error);
      return;
    }
    if (Object.keys(result.changes).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(
        openedVersion
          ? { ...result.changes, version: openedVersion }
          : result.changes,
      );
      onClose();
    } catch (err) {
      const conflict =
        (err as { response?: { status?: number } })?.response?.status === 409;
      setStale(conflict);
      setError(
        conflict
          ? 'Someone else saved these settings while this window was open. Close it and open it again to see their change, then make yours.'
          : lifecycleErrorMessage(err, 'Could not save the settings.'),
      );
      setSaving(false);
    }
  };

  return (
    <Modal
      disableClickout
      size={size}
      title={title}
      onRequestClose={saving ? undefined : onClose}
    >
      <div data-testid={testId}>
        <Body>
          {children}
          {error && (
            <div data-testid={`${testId}Error`} role="alert">
              <Notice tone="red">{error}</Notice>
            </div>
          )}
        </Body>
        <Actions>
          <Button
            a11yLabel="Cancel"
            appearance="outline"
            disabled={saving}
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel="Save"
            appearance="primary"
            disabled={stale}
            isLoading={saving}
            label="Save"
            onClick={save}
          />
        </Actions>
      </div>
    </Modal>
  );
};

export default SettingsDialog;
